/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2023 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/

"use client";
import ImageComponent from "@/components/ImageGrid/ImageComponent";
import { Flex, Button, Content, Heading, IllustratedMessage, View, ProgressCircle } from '@adobe/react-spectrum';
import React, { useEffect, useState } from 'react';
import NotFound from '@spectrum-icons/illustrations/NotFound';
import style from './image-grid.module.scss';
import dynamic from "next/dynamic";
import { sendAdobeAnalyticsLoadMoreEvent } from '@/utils/utils';
import { useTranslations } from "next-intl";
import { usePathname } from 'next/navigation'
import { sendAdobeAnalyticsReferrer } from '@/utils/utils';
// import { generateToken } from "@/utils/fetch_utility";


const CustomGrid: any = dynamic(() => import('@/libs/PSX-Grid'), { ssr: false });

const imageGrid = ({ query, imageData, nextCursor, locale, token }: { query?: string, imageData: any, nextCursor: string, locale: string, token?: string }) => {

    const [images, setImages] = useState<any[]>(imageData);
    const [cursor, setCursor] = useState(false);
    const [href, setHref] = useState(nextCursor);
    const t = useTranslations("");
    const path = usePathname();

    const [loader, setLoader] = useState(false);

    // const [text, setText] = useState({ viewMore: "View more", noResult: "No results", tryAnother: "Try another search" });

    useEffect(()=>{
        const pathArr = path.split('/');
        pathArr.pop();
        const pathURL= pathArr.join('/');
        
        if(pathURL == '/en/discover/category'){
            sendAdobeAnalyticsReferrer();
        }
    },[])

    useEffect(() => {
        setImages(imageData);
        const timeout = setTimeout(() => hasCursorParam(nextCursor), 200);
        return () => {
            clearTimeout(timeout);
        }
    }, [imageData, query, locale]);


    const fetchImages = async () => {
        setLoader(true);
        try {
            const response = await fetch(
                href,
                {
                    headers: {
                        'x-api-key': 'KulerBackendClientId',
                        Authorization: `Bearer ${token}`
                    },
                }
            );
            const data = await response.json();

            const newImages = data._embedded.assets.map((asset: any) => ({
                id: asset.id,
                locale,
                after_url: `https://${process.env.NEXT_PUBLIC_cpApiHost}/${process.env.NEXT_PUBLIC_dcx_path}/${asset.id}/rendition/images/initial/version/0/format/jpg/dimension/width/size/400`,
                url: asset._links.rendition?.href.replace('{format}', 'jpg').replace('{dimension}', 'width').replace('{size}', '400'),
                owner: asset._embedded.owner._links.images[5]?.href,
                owner_id: asset._embedded.owner.id,
            }));
            console.info(newImages[0])
            setImages(prevImages => [...prevImages, ...newImages]);
            setHref(data._links.next?.href);
            hasCursorParam(data._links.next?.href);
            setLoader(false);
        } catch (error) {
            console.error('Error fetching images:', error);
        }
    };

    async function loadMoreImages() {
        sendAdobeAnalyticsLoadMoreEvent();
        // const { data: token } = await generateToken();
        // const token = "eyJhbGciOiJSUzI1NiIsIng1dSI6Imltc19uYTEta2V5LWF0LTEuY2VyIiwia2lkIjoiaW1zX25hMS1rZXktYXQtMSIsIml0dCI6ImF0In0.eyJpZCI6IjE3MDc3NDUxMzUxOTVfNGFlYTkzZjMtZDNhMS00YWIzLWIzYjgtYWNiYTBlOGUwZjU5X3V3MiIsInR5cGUiOiJhY2Nlc3NfdG9rZW4iLCJjbGllbnRfaWQiOiJLdWxlckJhY2tlbmRDbGllbnRJZCIsInVzZXJfaWQiOiJLdWxlckJhY2tlbmRDbGllbnRJZEBBZG9iZUlEIiwiYXMiOiJpbXMtbmExIiwiYWFfaWQiOiJLdWxlckJhY2tlbmRDbGllbnRJZEBBZG9iZUlEIiwiY3RwIjowLCJwYWMiOiJLdWxlckJhY2tlbmRDbGllbnRJZCIsInJ0aWQiOiIxNzA3NzQ1MTM1MTk1XzFjNzY0NWRmLWY1NjYtNGUyZS1iZDdlLTFiZDQ5ZWQ2NGJiYV91dzIiLCJtb2kiOiI1NjAwZTg4NyIsInJ0ZWEiOiIxNzA4OTU0NzM1MTk1IiwiZXhwaXJlc19pbiI6Ijg2NDAwMDAwIiwic2NvcGUiOiJzeXN0ZW0iLCJjcmVhdGVkX2F0IjoiMTcwNzc0NTEzNTE5NSJ9.VCjIncxpAnzq4aFtTk1NTpIgaCNaUjfvC-8CO0r1O0pTvR6inkwe1CGdH4HP1F3nkGEsE3PqvbaWeXuDTGjtFoh24RAKuqTw8x__AthyzqAKTbNa7Y7-aub3XHLKFa874JwwaNKRDKKNrizILJMzVdqfDpCBWZPylKT9KudYcBg2lx20FzypHK2LQ4JqCfeVr0Rjwo6Mv6tRR4HDCRh5CFleoH_QanS22OQzXOzFD5Ni-pOK_n2pyz1R0BHyVrIdo89NkDhOJaR5aGWgGFPLU7I8hlosfRVS8f9f73j7XuzCEX_m-oLLcV7zcWCYAmqWAoJcB57IKPet2kTYS9LreQ";
        fetchImages();
    };

    const hasCursorParam = (url: string) => {
        const regex = /[?&]cursor=/;
        setCursor(regex.test(url));
    }

    if (!images || images.length === 0) {
        return (
            <Flex direction={"column"} justifyContent={"center"} flexGrow={1}>
                <IllustratedMessage >
                    <NotFound />
                    <Heading>{t("no_result")}</Heading>
                    <Content>{t("try_another_search")}</Content>
                </IllustratedMessage>
            </Flex>
        )
    }

    return (
        <View UNSAFE_className={style.main}>
            <View UNSAFE_className="container">
                <CustomGrid>
                    {images.map((image) => (
                        <ImageComponent key={image.id} item={image} category={query} locale={locale} />
                    ))}
                </CustomGrid>
            </View>
            <div className={style.overlay}>

                <Flex justifyContent={"center"} >
                    {loader ? (
                       <Flex justifyContent={"center"} minHeight={"100%"} alignItems={"center"}>
                            <ProgressCircle aria-label="Loading…" value={50} size="L" isIndeterminate />
                        </Flex>
                    ) :
                        cursor && (
                            <Flex justifyContent={"center"} marginTop={25}>
                                <Button UNSAFE_className={style.viewMoreBtn} variant="secondary" zIndex={5} onPress={loadMoreImages}>
                                    {t("view_more")}
                                </Button>
                            </Flex>
                        )}
                </Flex>
            </div>

        </View>
    );

};

export default imageGrid;